import { useState, useEffect } from 'react';
import { ArrowLeft, Search, Filter, Download, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

const AuditTrail = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [auditEvents, setAuditEvents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [totalEvents, setTotalEvents] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    if (user) {
      fetchAuditEvents();
    }
  }, [user, currentPage]);

  const fetchAuditEvents = async () => {
    try {
      const startIndex = (currentPage - 1) * itemsPerPage;
      
      const { data, error, count } = await supabase
        .from('audit_events')
        .select('*', { count: 'exact' })
        .order('timestamp', { ascending: false })
        .range(startIndex, startIndex + itemsPerPage - 1);

      if (error) throw error;

      // Fetch actor names separately
      if (data && data.length > 0) {
        const actorIds = [...new Set(data.map(e => e.actor_id))];
        const { data: actors } = await supabase
          .from('users_public')
          .select('id, name')
          .in('id', actorIds);

        const actorMap = new Map(actors?.map(a => [a.id, a.name]));
        const enrichedData = data.map(event => ({
          ...event,
          actor: { name: actorMap.get(event.actor_id) || 'Unknown User' }
        }));
        
        setAuditEvents(enrichedData);
      } else {
        setAuditEvents(data || []);
      }

      setTotalEvents(count || 0);
    } catch (error: any) {
      toast({
        title: "Error loading audit events",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const getCategoryColor = (entityType: string) => {
    switch (entityType) {
      case 'checklists': return 'bg-green-500/20 text-green-300';
      case 'incidents': return 'bg-red-500/20 text-red-300';
      case 'users_public': return 'bg-purple-500/20 text-purple-300';
      case 'checklist_templates': return 'bg-blue-500/20 text-blue-300';
      case 'units': return 'bg-orange-500/20 text-orange-300';
      default: return 'bg-gray-500/20 text-gray-300';
    }
  };

  const getEventTypeColor = (eventType: string) => {
    switch (eventType) {
      case 'create': return 'bg-green-500/20 text-green-300';
      case 'update': return 'bg-blue-500/20 text-blue-300';
      case 'delete': return 'bg-red-500/20 text-red-300';
      default: return 'bg-gray-500/20 text-gray-300';
    }
  };

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="container mx-auto max-w-6xl">
        <div className="mb-8">
          <Link to="/dashboard" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4">
            <ArrowLeft className="h-4 w-4" />
            Back to Dashboard
          </Link>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="heading-section mb-2">Audit Trail</h1>
              <p className="text-muted-foreground">Complete audit history of all system activities</p>
            </div>
            <Button className="cta-primary">
              <Download className="h-4 w-4 mr-2" />
              Export Audit Log
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card className="glass-panel border-0 mb-6">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                  <Input 
                    placeholder="Search audit events..." 
                    className="glass-input pl-10"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" className="glass-button">
                  <Filter className="h-4 w-4 mr-2" />
                  Filter by Date
                </Button>
                <Button variant="outline" className="glass-button">
                  <Filter className="h-4 w-4 mr-2" />
                  Filter by Site
                </Button>
                <Button variant="outline" className="glass-button">
                  <Filter className="h-4 w-4 mr-2" />
                  Filter by User
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Audit Events */}
        <div className="space-y-4">
          {loading ? (
            Array.from({ length: 5 }).map((_, i) => (
              <Card key={i} className="glass-panel border-0">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <Skeleton className="h-10 w-10 rounded-lg" />
                    <div className="flex-1">
                      <Skeleton className="h-5 w-48 mb-2" />
                      <Skeleton className="h-4 w-full mb-2" />
                      <Skeleton className="h-3 w-64" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          ) : auditEvents.length === 0 ? (
            <div className="text-center py-16">
              <Clock className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-2xl font-bold mb-2">No audit events yet</h3>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                System activities will be tracked and displayed here as you use the platform.
              </p>
            </div>
          ) : (
            auditEvents.map((event) => (
              <Card key={event.id} className="glass-panel border-0">
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4 flex-1">
                      <div className="p-2 glass-button rounded-lg">
                        <Clock className="h-4 w-4" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-semibold capitalize">{event.action}</h3>
                          <Badge className={getCategoryColor(event.entity_type)}>
                            {event.entity_type.replace('_', ' ')}
                          </Badge>
                          <Badge className={getEventTypeColor(event.event_type)}>
                            {event.event_type}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground mb-2">
                          {event.entity_type} {event.event_type} operation
                        </p>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <span>User: {event.actor?.name || 'System'}</span>
                          <span>Time: {new Date(event.timestamp).toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                    <Button size="sm" variant="outline" className="glass-button">
                      View Details
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Pagination */}
        <Card className="glass-panel border-0 mt-8">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                Showing {Math.min(itemsPerPage, auditEvents.length)} of {totalEvents} audit events
              </div>
              {totalEvents > itemsPerPage && (
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="glass-button"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  >
                    Previous
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="glass-button"
                    disabled={currentPage * itemsPerPage >= totalEvents}
                    onClick={() => setCurrentPage(prev => prev + 1)}
                  >
                    Next
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AuditTrail;